import {useQuery} from '@apollo/client';
import {Alert, Button, Select, Table, Tooltip, Typography} from 'antd';
import moment from 'moment';
import {useState} from 'react';
import {GET_AUTOMATIC_KYC_QUEUE} from 'graphql/queries/users';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  buildQueueCsv,
  confirmQueueExport,
  humanizeEnum,
  kycStatusLabel,
  useCanExportKyc,
  kycTabHref,
  KycUserLink,
  renderKycStatusTag,
  renderKycStatusTagWithReason,
} from './kycQueueShared';
import {
  EMPTY_CHECK_FILTERS,
  EMPTY_QUEUE_FILTERS,
  KycCheckFilters,
  KycCountryFilter,
  KycQueueFilterSummary,
  KycQueueFilters,
  QueueFilterField,
  buildCheckFilterInput,
  buildKycFilterInput,
} from './kycQueueFilters';
import styles from './kycDashboard.module.scss';
import {useKycNotify} from './kycNotifyAction';
import {ExportButton} from 'components/button/ExportButton/ExportButton';

const PROVIDER_OPTIONS = [
  {value: 'COMPLYCUBE', label: 'ComplyCube'},
  {value: 'SMILEID', label: 'Smile ID'},
];

const PROVIDER_LABEL = {
  COMPLYCUBE: 'ComplyCube',
  SMILEID: 'Smile ID',
};

// Cards predating the provider field have none stored. The server counts those
// as ComplyCube when you filter by provider, so label them the same way —
// showing "-" would put rows in the ComplyCube filter that read as unknown.
const describeProvider = provider =>
  PROVIDER_LABEL[provider] ||
  (provider ? humanizeEnum(provider) : 'ComplyCube');

const fullNameOf = user =>
  `${user?.firstName || ''} ${user?.lastName || ''}`.trim();

const documentTypeOf = record =>
  humanizeEnum(record?.providerDocumentType || record?.documentType);

const submittedAtOf = record =>
  record?.kycInitiatedAt
    ? moment(record.kycInitiatedAt).format('DD MMM YYYY, HH:mm')
    : '-';

const {Title, Text} = Typography;

export const AutomaticKycQueueTab = () => {
  const {canNotify, openKycNotify, kycNotifyModals} = useKycNotify();
  const canExport = useCanExportKyc();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [provider, setProvider] = useState(undefined);
  const [checks, setChecks] = useState(EMPTY_CHECK_FILTERS);
  const [filters, setFilters] = useState(EMPTY_QUEUE_FILTERS);

  // Any filter change renumbers the results, so never keep the old page.
  const applyFilter = setter => value => {
    setter(value);
    setPage(1);
  };

  // The status selectors each send a patch for their own field.
  const applyChecks = patch => {
    setChecks(current => ({...current, ...patch}));
    setPage(1);
  };

  // Country patches from the row, and whole objects from the modal and the
  // summary's tags — merging covers both without either clobbering the other.
  const applyFilters = patch => {
    setFilters(current => ({...current, ...patch}));
    setPage(1);
  };

  // errorPolicy 'all' so an unpublished gateway schema shows an empty queue
  // rather than taking the whole KYC page down.
  const {data, loading, error, refetch} = useQuery(GET_AUTOMATIC_KYC_QUEUE, {
    variables: {
      page,
      limit: pageSize,
      filters: {
        provider,
        // The identity/document/AML/deposit lists and the five shared attribute
        // filters go in the same input, all AND-ed with the provider.
        ...buildCheckFilterInput(checks),
        ...buildKycFilterInput(filters),
      },
    },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const payload = data?.getAutomaticKycVerificationRequests;
  // The field is a union: the Error branch carries a message instead of rows.
  const queueError =
    payload?.__typename === 'Error' ? payload.message : error?.message;
  const requests = payload?.requests || [];
  const totalDocs = payload?.totalDocs || 0;

  const handleTableChange = pagination => {
    if (pagination?.pageSize && pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      // A new page size renumbers the pages, so go back to the first one
      // rather than landing past the end of a shorter list.
      setPage(1);
      return;
    }
    setPage(pagination?.current || 1);
  };

  const columns = [
    {
      title: 'Full Name',
      key: 'fullName',
      // Pinned: the row is wider than the screen, and scrolling out to the
      // Smile ID or AML column otherwise leaves a row of statuses with nothing
      // saying whose they are. A fixed column needs an explicit width.
      fixed: 'left',
      width: 180,
      render: (_, record) => (
        <KycUserLink user={record?.user}>
          {fullNameOf(record?.user)}
        </KycUserLink>
      ),
      exportValue: record => fullNameOf(record?.user),
    },
    {
      title: 'Email',
      key: 'email',
      render: (_, record) => (
        <KycUserLink user={record?.user}>{record?.user?.email}</KycUserLink>
      ),
      exportValue: record => record?.user?.email || '-',
    },
    {
      title: 'Country',
      key: 'country',
      render: (_, record) => record?.user?.countryISO || '-',
      exportValue: record => record?.user?.countryISO || '-',
    },
    {
      title: 'Provider',
      key: 'provider',
      render: (_, record) => describeProvider(record?.provider),
      exportValue: record => describeProvider(record?.provider),
    },
    {
      title: 'Document Type',
      key: 'documentType',
      render: (_, record) => documentTypeOf(record),
      exportValue: documentTypeOf,
    },
    {
      title: 'Submitted At',
      key: 'kycInitiatedAt',
      render: (_, record) => submittedAtOf(record),
      exportValue: submittedAtOf,
    },
    {
      title: 'KYC Status',
      key: 'kycStatus',
      exportValue: record => kycStatusLabel(record?.kycStatus),
      render: (_, record) =>
        renderKycStatusTagWithReason(
          record?.kycStatus,
          record?.rejectionCode,
          record?.rejectionMessages,
        ),
    },
    {
      title: 'AML',
      key: 'amlStatus',
      render: (_, record) => renderKycStatusTag(record?.amlStatus),
      exportValue: record => kycStatusLabel(record?.amlStatus),
    },
    {
      title: 'Document Check',
      key: 'documentCheckStatus',
      exportValue: record => kycStatusLabel(record?.documentCheckStatus),
      // The two booleans behind the status: which half of the check passed
      // decides whether a reviewer looks at the document or the selfie.
      render: (_, record) => {
        const tag = renderKycStatusTag(record?.documentCheckStatus);
        const hint = [
          typeof record?.isDocumentVerified === 'boolean'
            ? `Document ${
                record.isDocumentVerified ? 'verified' : 'not verified'
              }`
            : null,
          typeof record?.isSelfieVerified === 'boolean'
            ? `Selfie ${record.isSelfieVerified ? 'verified' : 'not verified'}`
            : null,
        ]
          .filter(Boolean)
          .join(' · ');

        // A span, not the tag itself: a legacy card can carry the two
        // booleans with no status at all, and then `tag` is plain text, which
        // Tooltip has nothing to attach to.
        return hint ? (
          <Tooltip title={hint}>
            <span>{tag}</span>
          </Tooltip>
        ) : (
          tag
        );
      },
    },
    {
      title: '',
      key: 'action',
      // Pinned to the other edge for the same reason: Review and Notify stay
      // reachable without scrolling back across every status column.
      fixed: 'right',
      width: 150,
      render: (_, record) => {
        if (!record?.user?.id) return null;
        return (
          <div className={styles.queueRowActions}>
            {/* An anchor rather than a navigate(): the reviewer works down the
                queue, so opening each submission in its own tab keeps this
                list, its filters and its page in place. It also restores
                middle-click, cmd-click and "copy link address", which a
                button-with-onClick silently swallows. */}
            <a
              className={styles.queueReviewLink}
              href={kycTabHref(record.user.id)}
              target="_blank"
              rel="noopener noreferrer">
              Review
            </a>
            {canNotify && (
              <button
                type="button"
                className={styles.queueNotifyButton}
                onClick={() => openKycNotify(record.user)}>
                Notify
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const csv = buildQueueCsv(columns, requests);

  return (
    <div className={styles.container}>
      <div className={styles.sectionIntro}>
        <Title level={4}>Automatic Review Queue</Title>
        <Text type="secondary">
          Users verified through ComplyCube or Smile ID rather than the manual
          fallback. The identity, AML and document checks are independent, so
          filter on each one separately — an approved identity check does not
          mean the AML screening passed. Open a row to inspect the
          provider&apos;s media and set the KYC status.
        </Text>
      </div>

      {queueError && (
        <Alert
          type="error"
          showIcon
          className={styles.queueError}
          message="The queue could not be loaded"
          description={`${queueError} — an empty table below does not mean there is nothing to review.`}
        />
      )}

      <div className={styles.queueActions}>
        <QueueFilterField label="Provider" htmlFor="automatic-provider">
          <Select
            id="automatic-provider"
            value={provider}
            onChange={applyFilter(setProvider)}
            options={PROVIDER_OPTIONS}
            allowClear
            placeholder="All"
          />
        </QueueFilterField>
        <KycCheckFilters value={checks} onChange={applyChecks} />
        <KycCountryFilter value={filters} onChange={applyFilters} />
        <div className={styles.queueActionsEnd}>
          {/* The page on screen, not the whole filtered queue — there is no
              server-side export yet and the browser only holds this page. */}
          {canExport && (
            <ExportButton
              csvHeaders={csv.headers}
              csvData={csv.data}
              fileName={`automatic-kyc-queue-page-${page}.csv`}
              label="Export"
              asyncOnClick
              onClick={confirmQueueExport(csv.data.length)}
            />
          )}
          <KycQueueFilters value={filters} onChange={applyFilters} />
          <Button onClick={() => refetch()} loading={loading}>
            Refresh
          </Button>
        </div>
      </div>

      <KycQueueFilterSummary value={filters} onChange={applyFilters} />

      <Table
        columns={columns}
        dataSource={requests}
        loading={loading}
        className="antd-table"
        onChange={handleTableChange}
        scroll={{x: 'max-content'}}
        pagination={{
          current: page,
          pageSize,
          total: totalDocs,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          showTotal: total => `${total} matching`,
        }}
        rowKey="id"
      />
      {kycNotifyModals}
    </div>
  );
};
